<template>
  <el-container class="layout">
    <el-header class="topbar">
      <el-button
        v-if="isMobile"
        class="nav-toggle"
        text
        aria-label="打开菜单"
        @click="drawerVisible = true"
      >
        <el-icon :size="22"><Menu /></el-icon>
      </el-button>

      <div class="logo">
        <img v-if="siteInfo.logo" :src="siteInfo.logo" class="logo-img" alt="logo" />
        <LogoIcon v-else :size="28" class="logo-icon" />
        <span>{{ siteInfo.name }}</span>
      </div>

      <el-menu
        v-if="!isMobile"
        class="nav"
        mode="horizontal"
        :ellipsis="false"
        :default-active="activeMenu"
        router
      >
        <el-menu-item v-for="m in menus" :key="m.path" :index="m.path">
          <el-icon><component :is="m.icon" /></el-icon><span>{{ m.label }}</span>
        </el-menu-item>
      </el-menu>

      <div class="header-right">
        <!-- 系统设置：胶囊菜单容器外的图标入口，紧贴登录用户左侧；超级管理员/演示账号可见 -->
        <el-dropdown v-if="!isMobile && canSeeSetting" trigger="click" @command="(cmd) => router.push(cmd)">
          <span class="setting-outside" :class="{ 'is-active': isSettingRoute }" title="系统设置">
            <el-icon><Setting /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item v-for="m in settingMenus" :key="m.path" :command="m.path">
                {{ m.label }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>

        <el-dropdown @command="handleCommand">
          <span class="admin-name">
            <el-avatar v-if="adminUser.avatar" :size="26" :src="adminUser.avatar" class="admin-avatar" />
            <el-icon v-else class="admin-avatar-icon"><UserFilled /></el-icon>
            {{ adminName }}<el-icon><ArrowDown /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <!-- 登录后返回的账号信息：账号 + 角色 -->
              <el-dropdown-item disabled class="admin-identity">
                {{ adminUser.username }} · {{ roleLabel }}
              </el-dropdown-item>
              <el-dropdown-item command="profile" divided>个人资料</el-dropdown-item>
              <el-dropdown-item command="password" divided>修改密码</el-dropdown-item>
              <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </el-header>

    <!-- 移动端竖向菜单抽屉 -->
    <el-drawer
      v-if="isMobile"
      v-model="drawerVisible"
      class="mobile-nav-drawer"
      direction="ltr"
      size="210px"
      :with-header="false"
    >
      <div class="drawer-brand">
        <img v-if="siteInfo.logo" :src="siteInfo.logo" class="logo-img" alt="logo" />
        <LogoIcon v-else :size="26" />
        <span>{{ siteInfo.name }}</span>
      </div>
      <el-menu class="drawer-menu" :default-active="activeMenu" router @select="drawerVisible = false">
        <el-menu-item v-for="m in menus" :key="m.path" :index="m.path">
          <el-icon><component :is="m.icon" /></el-icon><span>{{ m.label }}</span>
        </el-menu-item>
        <!-- 系统设置：一级菜单带图标，二级菜单默认折叠；超级管理员/演示账号可见 -->
        <el-sub-menu v-if="canSeeSetting" index="/setting">
          <template #title>
            <el-icon><Setting /></el-icon><span>系统设置</span>
          </template>
          <el-menu-item v-for="m in settingMenus" :key="m.path" :index="m.path">
            <span>{{ m.label }}</span>
          </el-menu-item>
        </el-sub-menu>
      </el-menu>

      <!-- 移动端：登录用户贴底居中，操作直接平铺 -->
      <div class="drawer-user">
        <el-avatar :size="52" :src="adminUser.avatar" class="drawer-user__avatar">
          <el-icon><UserFilled /></el-icon>
        </el-avatar>
        <div class="drawer-user__name">{{ adminName }}</div>
        <div class="drawer-user__role">{{ roleLabel }}</div>
        <div class="drawer-user__links">
          <span @click="drawerVisible = false; openProfile()">个人资料</span>
          <i class="sep"></i>
          <span @click="drawerVisible = false; handleCommand('password')">修改密码</span>
          <i class="sep"></i>
          <span class="logout" @click="drawerVisible = false; handleCommand('logout')">退出登录</span>
        </div>
      </div>
    </el-drawer>

    <el-dialog v-model="pwdVisible" title="修改密码" width="420px" :close-on-click-modal="false" @closed="resetPwdForm">
      <el-form ref="pwdFormRef" :model="pwdForm" :rules="pwdRules" label-width="80px">
        <el-form-item label="原密码" prop="oldPassword">
          <el-input v-model="pwdForm.oldPassword" type="password" placeholder="请输入原密码" show-password />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="pwdForm.newPassword" type="password" placeholder="请输入新密码（至少 6 位）" show-password />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="pwdForm.confirmPassword" type="password" placeholder="请再次输入新密码" show-password />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="pwdVisible = false">取消</el-button>
        <el-button type="primary" color="#001529" :loading="pwdLoading" @click="submitPassword">确定</el-button>
      </template>
    </el-dialog>

    <!-- 个人资料：只能改昵称与头像（头像上传时自动压缩） -->
    <el-drawer
      v-model="profileVisible"
      :size="isMobile ? '92%' : '420px'"
      class="tpl-drawer"
      header-class="au-drawer-header"
      :show-close="false"
      @closed="discardUploads"
    >
      <template #header>
        <div class="au-drawer__head">
          <span class="au-drawer__title">个人资料</span>
          <el-button link class="au-drawer__close" @click="profileVisible = false">
            <el-icon><Close /></el-icon>
          </el-button>
        </div>
      </template>
      <el-form label-width="70px">
        <el-form-item label="头像">
          <ImageUpload v-model="profileForm.avatar" dir="avatars" :max-edge="400" :target-kb="100" circle />
        </el-form-item>
        <el-form-item label="昵称">
          <el-input v-model="profileForm.nickname" maxlength="20" show-word-limit placeholder="显示在顶部导航栏的名称" />
        </el-form-item>
        <el-form-item label="账号">
          <el-input :model-value="adminUser.username" disabled />
          <div class="au-tip">账号与角色不可自行修改</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="profileVisible = false">取消</el-button>
        <el-button type="primary" color="#001529" :loading="profileSaving" @click="saveProfile">保存</el-button>
      </template>
    </el-drawer>

    <el-main class="main">
      <router-view />
    </el-main>
  </el-container>
</template>

<script setup>
import { computed, reactive, ref, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessageBox, ElMessage } from 'element-plus'
import {
  DataLine, Goods, List, Wallet, User, Ticket, Setting, Menu, ChatDotRound, RefreshLeft, UserFilled, Close
} from '@element-plus/icons-vue'
import LogoIcon from '../components/LogoIcon.vue'
import ImageUpload from '../components/ImageUpload.vue'
import { changePassword, getAdminProfile, updateAdminProfile } from '../api'
import { siteInfo, loadSiteInfo } from '../utils/siteInfo'
import { discardUploads } from '../utils/pendingUploads'

const route = useRoute()
const router = useRouter()

// 登录后落库的管理员信息（头像/账号/昵称/角色）；进页面再向后端刷新一次，角色变更即时生效
const adminUser = ref(JSON.parse(localStorage.getItem('adminUser') || '{}'))
const adminName = computed(() => adminUser.value.nickname || adminUser.value.username || '管理员')
// 系统设置对「超级管理员」和「演示账号」都可见（演示账号只读，仅保存等写操作被拦截）
const canSeeSetting = computed(() => ['super', 'demo'].includes(adminUser.value.role))
const roleLabel = computed(() => {
  if (adminUser.value.role === 'super') return '超级管理员'
  if (adminUser.value.role === 'demo') return '演示账号'
  return '普通用户'
})

function loadProfile() {
  getAdminProfile()
    .then((res) => {
      adminUser.value = res.data || {}
      localStorage.setItem('adminUser', JSON.stringify(res.data || {}))
    })
    .catch(() => {})
}

const activeMenu = computed(() => route.path)

// 顶部导航菜单（桌面端横排胶囊容器内的项）
// 系统设置单独放在胶囊容器外（下拉入口），所以不在这里
const menus = [
  { path: '/dashboard', label: '数据看板', icon: DataLine },
  { path: '/product', label: '商品管理', icon: Goods },
  { path: '/order', label: '订单管理', icon: List },
  { path: '/afterSale', label: '售后管理', icon: RefreshLeft },
  { path: '/comment', label: '评价管理', icon: ChatDotRound },
  { path: '/pay', label: '支付管理', icon: Wallet },
  { path: '/user', label: '用户管理', icon: User },
  { path: '/coupon', label: '优惠券管理', icon: Ticket }
]

// 系统设置下拉里的各项：都是独立页面，且按需求不带图标
const settingMenus = [
  { path: '/setting/siteinfo', label: '系统信息' },
  { path: '/setting/banner', label: '首页轮播' },
  { path: '/setting/pay', label: '支付设置' },
  { path: '/setting/service', label: '客服设置' },
  { path: '/setting/recharge', label: '充值配置' },
  { path: '/setting/member', label: '会员政策' },
  { path: '/setting/adminUser', label: '账号管理' }
]

// 系统设置页（含各分区）时，容器外的「系统设置」入口高亮
const isSettingRoute = computed(() => route.path.startsWith('/setting'))

// 移动端（<=768px）：隐藏横排菜单，改用汉堡按钮 + 抽屉竖向菜单
const isMobile = ref(typeof window !== 'undefined' && window.innerWidth <= 768)
const drawerVisible = ref(false)

function onResize() {
  isMobile.value = window.innerWidth <= 768
  if (!isMobile.value) drawerVisible.value = false
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  loadProfile()
  // 顶部导航栏的系统名称 / Logo 与标签页图标
  loadSiteInfo()
})
onBeforeUnmount(() => window.removeEventListener('resize', onResize))

// ---------- 修改密码 ----------
const pwdVisible = ref(false)
const pwdLoading = ref(false)
const pwdFormRef = ref()
const pwdForm = reactive({ oldPassword: '', newPassword: '', confirmPassword: '' })
const pwdRules = {
  oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, message: '新密码长度不能少于 6 位', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: '请再次输入新密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value !== pwdForm.newPassword) callback(new Error('两次输入的密码不一致'))
        else callback()
      },
      trigger: 'blur'
    }
  ]
}

function resetPwdForm() {
  pwdFormRef.value?.resetFields()
}

async function submitPassword() {
  await pwdFormRef.value.validate(async (valid) => {
    if (!valid) return
    pwdLoading.value = true
    try {
      await changePassword({ oldPassword: pwdForm.oldPassword, newPassword: pwdForm.newPassword })
      ElMessage.success('密码修改成功，请重新登录')
      pwdVisible.value = false
      localStorage.removeItem('adminToken')
      localStorage.removeItem('adminUser')
      router.push('/login')
    } catch (e) {
      // 拦截器已提示
    } finally {
      pwdLoading.value = false
    }
  })
}

// ---------- 个人资料（仅昵称 + 头像，头像上传时自动压缩） ----------
const profileVisible = ref(false)
const profileSaving = ref(false)
const profileForm = reactive({ nickname: '', avatar: '' })

// 打开时用当前登录信息回填
function openProfile() {
  profileForm.nickname = adminUser.value.nickname || ''
  profileForm.avatar = adminUser.value.avatar || ''
  profileVisible.value = true
}

async function saveProfile() {
  profileSaving.value = true
  try {
    await updateAdminProfile({ nickname: profileForm.nickname, avatar: profileForm.avatar })
    // 本地同步：顶部导航栏立即显示新昵称/头像
    adminUser.value = { ...adminUser.value, nickname: profileForm.nickname, avatar: profileForm.avatar }
    localStorage.setItem('adminUser', JSON.stringify(adminUser.value))
    ElMessage.success('资料已更新')
    profileVisible.value = false
  } catch (e) {
    // 拦截器已提示
  } finally {
    profileSaving.value = false
  }
}

function handleCommand(command) {
  if (command === 'profile') {
    openProfile()
  } else if (command === 'password') {
    pwdVisible.value = true
  } else if (command === 'logout') {
    ElMessageBox.confirm('确认退出登录？', '提示', {
      confirmButtonText: '退出',
      cancelButtonText: '取消',
      type: 'warning'
    })
      .then(() => {
        localStorage.removeItem('adminToken')
        localStorage.removeItem('adminUser')
        ElMessage.success('已退出登录')
        router.push('/login')
      })
      .catch(() => {})
  }
}
</script>

<style scoped>
.layout {
  height: 100vh;
}
.topbar {
  display: flex;
  align-items: center;
  height: 60px;
  padding: 0 20px;
  /* 参照 wanhuiOnline 顶栏：左深右浅的横向渐变 + 底部细线 */
  background: linear-gradient(90deg, #d2dcf7 0%, #e8edfb 45%, #f4f7fd 100%);
  border-bottom: 1px solid #dcdfe6;
}
.logo {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px 0 0;
  color: #1d2129;
  font-size: 16px;
  font-weight: bold;
  white-space: nowrap;
}
/* 系统名称：渐变文字 + 呼吸动效 */
.logo span {
  background: linear-gradient(90deg, #1677ff 0%, #13a8a8 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: logo-breathe 3s ease-in-out infinite;
}
@keyframes logo-breathe {
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.3); }
}
.logo-icon {
  font-size: 20px;
  color: #1677ff;
  animation: logo-icon-breathe 3s ease-in-out infinite;
  transition: transform 0.3s ease;
  cursor: pointer;
}
/* 自定义系统 Logo：与内置图标同样式，尺寸固定不撑高顶栏 */
.logo-img {
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
  animation: logo-icon-breathe 3s ease-in-out infinite;
  transition: transform 0.3s ease;
  cursor: pointer;
}
.logo-icon:hover,
.logo-img:hover {
  animation-play-state: paused;
  transform: rotate(12deg) scale(1.15);
}
/* Logo 图标呼吸：仅轻微缩放，与系统名称呼吸节奏同步（无光晕） */
@keyframes logo-icon-breathe {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
}
/* 顶部菜单：白底圆角框容器（36px），菜单项是框内的胶囊分段按钮 */
.nav {
  box-sizing: border-box;
  height: 36px;
  /* 覆盖 el-menu 横向模式默认 60px 行高 */
  --el-menu-horizontal-height: 28px;
  --el-menu-item-height: 28px;
  --el-menu-active-color: #409eff;
  border: 1px solid #e0e3e8;
  border-radius: 10px;
  padding: 2px;
  background: #fff;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  flex-shrink: 0;
  /* 菜单项之间的间距，hover/激活胶囊不贴在一起 */
  gap: 4px;
  max-width: 62vw;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  /* 菜单整体靠右：紧挨「系统设置」齿轮图标左侧，不再贴着标题 */
  margin-left: auto;
}
.nav::-webkit-scrollbar {
  display: none;
}
.nav :deep(.el-menu-item) {
  height: 28px;
  line-height: 28px;
  margin: 0;
  padding: 0 13px;
  border-radius: 7px;
  border-bottom: 2px solid transparent !important;
  background: transparent;
  color: #4e5969;
  font-size: 13px;
  white-space: nowrap;
}
.nav :deep(.el-menu-item .el-icon) {
  width: auto;
  margin-right: 4px;
  font-size: 14px;
}
.nav :deep(.el-menu-item:hover) {
  background: #f2f3f5;
  color: #1d2129;
}
.nav :deep(.el-menu-item.is-active) {
  background: #e6f1fe !important;
  color: #409eff !important;
  font-weight: 600;
}
.nav :deep(.el-menu-item.is-active .el-icon) {
  color: #409eff;
}
/* 胶囊容器外的独立入口（系统设置）：只显示齿轮图标，紧贴登录用户左侧 */
.setting-outside {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-right: 4px;
  border-radius: 8px;
  color: #4e5969;
  font-size: 16px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s, color 0.2s;
}
.setting-outside:hover {
  background: #f2f3f5;
  color: #1d2129;
}
.setting-outside.is-active {
  background: #e6f1fe;
  color: #409eff;
}
.header-right {
  flex: 0 0 auto;
  /* 菜单已用 margin-left:auto 右对齐，这里紧跟菜单右侧即可 */
  margin-left: 0;
  padding-left: 10px;
  /* 齿轮图标与用户名必须同一行、垂直居中，否则会被挤换行且两者高度不齐 */
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.header-right .admin-name {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  /* 顶栏改为浅色渐变，用户名改用深灰 */
  color: #4e5969;
}
/* 顶栏下拉触发器（齿轮 / 用户）：点击后浏览器会给带 tabindex 的触发器画聚焦框线，去掉 */
.header-right .admin-name,
.header-right .setting-outside,
.header-right :deep(.el-dropdown),
.header-right :deep(.el-tooltip__trigger),
.header-right .admin-name:focus,
.header-right .admin-name:focus-visible,
.header-right .setting-outside:focus,
.header-right .setting-outside:focus-visible,
.header-right :deep(.el-dropdown:focus),
.header-right :deep(.el-dropdown:focus-visible),
.header-right :deep(.el-tooltip__trigger:focus),
.header-right :deep(.el-tooltip__trigger:focus-visible) {
  outline: none !important;
  box-shadow: none;
}
/* 顶栏头像：默认无头像时用图标兜底，避免用户名位置跳动 */
.header-right .admin-avatar {
  flex-shrink: 0;
  background: #eef1f5;
}
/* 头像用居中裁剪填充，避免非正方形头像被拉伸变形，与上传预览一致 */
.header-right .admin-avatar :deep(img) {
  object-fit: cover;
}
.header-right .admin-avatar-icon {
  font-size: 18px;
  color: #8c939d;
}
/* 下拉里的账号身份行：不可点击，仅展示「账号 · 角色」 */
.admin-identity {
  height: auto;
  padding-top: 4px;
  padding-bottom: 6px;
  font-size: 12px;
  color: #909399;
}
.main {
  background: #f0f2f5;
  padding: 18px;
}
.drawer-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 6px 12px;
  font-size: 15px;
  font-weight: bold;
  color: #001529;
}
/* 抽屉内菜单去掉默认的右侧分割线，压缩菜单项高度以缩小间距 */
.drawer-menu {
  border-right: none !important;
}
.drawer-menu :deep(.el-menu-item) {
  height: 42px;
  line-height: 42px;
}
.drawer-menu :deep(.el-sub-menu__title) {
  height: 42px;
  line-height: 42px;
}
/* 抽屉内容纵向排布，便于把用户信息顶到底部 */
.mobile-nav-drawer :deep(.el-drawer__body) {
  display: flex;
  flex-direction: column;
  padding: 16px 12px 0;
}
/* 抽屉底部：登录用户居中 + 上分割线 + 操作平铺 */
.drawer-user {
  margin-top: auto;
  padding: 14px 6px 16px;
  border-top: 1px solid #ebeef5;
  text-align: center;
}
.drawer-user__avatar {
  margin-bottom: 10px;
  background: #f0f2f5;
  color: #b1b3b8;
  --el-avatar-size: 52px;
}
.drawer-user__name {
  font-size: 14px;
  font-weight: 600;
  color: #001529;
}
.drawer-user__role {
  margin-top: 2px;
  font-size: 12px;
  color: #909399;
}
.drawer-user__links {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  font-size: 13px;
}
.drawer-user__links span {
  cursor: pointer;
  color: #606266;
}
.drawer-user__links span:active {
  opacity: 0.6;
}
.drawer-user__links .logout {
  color: #f56c6c;
}
.drawer-user__links .sep {
  width: 1px;
  height: 12px;
  background: #dcdfe6;
}
/* 窄一些的 PC（常见 1280/1366/1440 笔记本）：收紧 logo 与右侧留白，
   菜单胶囊放不下时可横向滚动，不做裁切 */
@media (max-width: 1440px) {
  .logo {
    padding-right: 12px;
    font-size: 15px;
  }
  .header-right {
    padding-left: 8px;
  }
}
/* 移动端：隐藏横排菜单，汉堡按钮 + 抽屉竖向菜单 */
@media (max-width: 768px) {
  .topbar {
    height: 52px;
    padding-right: 12px;
  }
  .nav-toggle {
    color: #4e5969;
    margin-left: 4px;
    padding: 6px;
  }
  .logo {
    padding: 0 12px 0 0;
    font-size: 14px;
  }
  /* 用户已移到抽屉底部，顶栏不再显示 */
  .header-right {
    display: none;
  }
  .main {
    padding: 10px;
  }
}
/* 个人资料抽屉：标题行样式，标题下分隔线由全局 header-class 控制 */
.au-drawer__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}
.au-drawer__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.au-drawer__close {
  padding: 0;
  height: auto;
  color: #909399;
  font-size: 16px;
}
.au-drawer__close:hover {
  color: #409eff;
}
.au-tip {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}
</style>

<!-- 抽屉头部会被 teleport 到 body，scoped 样式无法命中 .el-drawer__header，
     故用全局 header-class 单独样式化（与商品/配送政策模板、用户详情一致） -->
<style>
.au-drawer-header {
  margin-bottom: 0 !important;
  padding-bottom: 14px;
  border-bottom: 1px solid #e4e7ed;
}
</style>
